/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { Chart } from 'chart.js/auto';
import React from 'react';
import { Category } from '../common/Categories';
import { fillDoughnutChartText, getXCoordinate } from '../common/common';
import './styles.css';

let percentData: Array<string> = [];
let imageNames: Array<string> = [];
let lastImageDrawn: any;
let errorImage: any;

const no_image = new Image();
no_image.src = 'https://atlas.trimble.com/AlmanacInstaller/Product/Images/noimage.png';
no_image.onload = () => {
    errorImage = no_image;
};

// Custom controller removed for Chart.js v4 compatibility.
// Use plugin to draw center labels and image.
const centerRenderPlugin = {
    id: 'centerRenderPlugin',
    beforeDraw(chart: any) {
        const { ctx, width, height } = chart;
        ctx.save();
        ctx.font = "15px Roboto Light, 'Open Sans', Arial, sans-serif";
        ctx.fillStyle = '#000';
        const cfg: any = chart.config.data;
        if (cfg.text === '') {
            const defaultTextCategory = cfg.defaultTextCategory;
            if (defaultTextCategory === 1) {
                const defaultText = 'Training Authorizations';
                const textArray: Array<string> = getDoughnutChartTextArray(defaultText);
                fillDoughnutChartText(textArray, defaultText, ctx, width, height);
            } else {
                const defaultText1 = 'Types of';
                ctx.fillText(defaultText1, getXCoordinate(defaultText1, width, ctx), height / 2.2);
                const defaultText2 = 'Devices Used';
                ctx.fillText(defaultText2, getXCoordinate(defaultText2, width, ctx), height / 1.8);
            }
            ctx.restore();
            return;
        }
        const percent = cfg.percent;
        if (percent) {
            ctx.fillText(percent, getXCoordinate(percent, width, ctx), height / 4.5);
        }
        const trimmedText = getTrimmedText(cfg.text);
        ctx.fillText(trimmedText, getXCoordinate(trimmedText, width, ctx), height / 1.2);
        drawCircle(ctx, width / 2, height / 2, 70);
        const imageName = cfg.imageName;
        if (imageName) {
            drawImage(ctx, imageName, width, height);
        }
        ctx.restore();
    }
};

function getDoughnutChartTextArray(text: string) {
    const textArray: Array<string> = [];
    const array1 = text.split(' ');
    let j = 0;
    for (const element of array1) {
        if (textArray[j] === undefined) {
            textArray[j] = '';
        }
        if (textArray[j].length < 15) {
            textArray[j] = `${textArray[j]  } ${  element}`;
        }
        else {
            j = j + 1;
            textArray[j] = element;
        }
    }
    return textArray;
}

function getTrimmedText(text: string) {

    while (text.length > 16) {
        text = text.substr(0, text.lastIndexOf('\ '));
    }
    return text;
}
function drawCircle(ctx: any, x: any, y: any, radius: any) {
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = '#fff';
    ctx.fill();    
}
function drawImage(ctx: any, imageName: any, chartWidth: any, chartHeight: any) {
    const base_image = new Image();
    base_image.src = 'https://atlas.trimble.com/AlmanacInstaller/Product/Images/' + imageName + '.jpg';
    let imageAttributes;

    if (lastImageDrawn !==undefined && lastImageDrawn.src === base_image.src) {
        imageAttributes = getImageAttributes(lastImageDrawn, chartWidth, chartHeight);
        ctx.drawImage(lastImageDrawn, imageAttributes.x, imageAttributes.y, imageAttributes.width, imageAttributes.height);
        return;
    }
    base_image.onload = () => {
        imageAttributes = getImageAttributes(base_image, chartWidth, chartHeight);
        ctx.drawImage(base_image, imageAttributes.x, imageAttributes.y, imageAttributes.width, imageAttributes.height);
        lastImageDrawn = base_image;
    };
    base_image.onerror = () => {
        if (base_image.src.search('.jpg') !== -1) {
            base_image.src = `https://atlas.trimble.com/AlmanacInstaller/Product/Images/${  imageName  }.png`;
            imageAttributes = getImageAttributes(base_image, chartWidth, chartHeight);
            ctx.drawImage(base_image, imageAttributes.x, imageAttributes.y, imageAttributes.width, imageAttributes.height);
        }
        else {
            imageAttributes = getImageAttributes(errorImage, chartWidth, chartHeight);
            ctx.drawImage(errorImage, imageAttributes.x, imageAttributes.y, imageAttributes.width, imageAttributes.height);
        }
    };
}

function getImageAttributes(base_image: any, chartWidth: any, chartHeight: any) {
    const maxwidth = 100;
    const maxheight = 100;
    let width = base_image !== undefined ? base_image.width : 100;
    let height = base_image.height !== undefined ? base_image.height:100;
    let ratio;
    if (width > maxwidth) {
        ratio = maxwidth / width;
        height = height * ratio;
        width = width * ratio;
    }

    if (height > maxheight) {
        ratio = maxheight / height;
        width = width * ratio;
        height = height * ratio;
    }

    const x = Math.round((chartWidth - width) / 2);
    const y = Math.round((chartHeight - height )/ 2);
    return { x, y, width, height }
}

const data = {
    labels: [],
    datasets: [{
        data: [],
        backgroundColor: [],
        borderColor: '#f3f3f7',
        hoverBackgroundColor: '#ffbe00',
        hoverBorderColor: '#ffbe00'
    }],
    text: '',
    percent: '',
    imageName:'',
    defaultTextCategory: 0
};
const options = {
    cutout: '79%',
    plugins: {
        legend: { display: false }
    },
    animation: { duration: 0 },
    elements: { arc: { borderWidth: 3 } },
    maintainAspectRatio: false
}

interface Props {
        label: Array<string>,
        data: Array<number>,
        imageNames: Array<string>,
        category: Category,
        defaultTextCategory?: number,
        height?: number
}
const grayShades: Array<string> = ['#bfbfc6', '#e2e2e7', '#d0d0d7'];
const colors: Array<string> = ['#FFE8CC', '#F4D1D3', '#FFF2CC', '#DFE9D6','#CFE1EE'];

let myDoughnutChart: any;
class DoughnutChart extends React.Component<Props> {
    private canvasRef = React.createRef<HTMLCanvasElement>();
    private cleanupHandlers: (() => void) | undefined;

    componentDidMount() {
        this.initializeChart(options);
    }
    componentWillUnmount() {
        if (this.cleanupHandlers) this.cleanupHandlers();
        if (myDoughnutChart) {
            try { myDoughnutChart.destroy(); } catch {}
        }
    }
    initializeChart(options: any) {
        data.text = data.percent = data.imageName = '';
        data.defaultTextCategory = this.props.defaultTextCategory ? this.props.defaultTextCategory : 0;
        data.labels = this.props.label as never[];
        data.datasets[0].data = this.props.data as never[];
        const total: number = this.props.data.reduce((a: number, b: number) => a + b, 0);
        percentData = this.props.data.map((x: number) => {
            return Math.abs(((x / total) * 100)).toFixed(1);
        });
        imageNames = this.props.imageNames as never[];

      const backgroundColors: Array<string> = [];
      
      this.props.data.forEach((value, index: number) => {
         backgroundColors.push(colors[index % colors.length]);
      });
      data.datasets[0].backgroundColor = backgroundColors as never[];
      


      const canvasEl = this.canvasRef.current;
      if (!canvasEl) return;
            myDoughnutChart = new Chart(canvasEl, {
                type: 'doughnut',
                data,
                options,
                plugins: [centerRenderPlugin]
            });

            const mouseMove = (evt: any) => {
                const elems = myDoughnutChart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, false);
                if (elems && elems.length > 0) {
                        const idx = elems[0].index;
                        const lbl = (data.labels && data.labels[idx]) ? data.labels[idx] : '';
                        data.text = lbl;
                        data.percent = percentData && percentData[idx] ? `${percentData[idx]}%` : '';
                        data.imageName = imageNames && imageNames[idx] ? imageNames[idx] : '';
                        myDoughnutChart.draw();
                } else {
                        data.text = '';
                        data.percent = '';
                        data.imageName = '';
                        myDoughnutChart.draw();
                }
            };
            const mouseLeave = () => {
                data.text = '';
                data.percent = '';
                data.imageName = '';
                myDoughnutChart.draw();
            };
            canvasEl.addEventListener('mousemove', mouseMove);
            canvasEl.addEventListener('mouseleave', mouseLeave);
            this.cleanupHandlers = () => {
                canvasEl.removeEventListener('mousemove', mouseMove);
                canvasEl.removeEventListener('mouseleave', mouseLeave);
            };

    }
    render() {
        const h = this.props.height ?? 300;
        return (
            <div>
                <canvas id="myDoughnutChart" ref={this.canvasRef} height={h} style={{ height: h }} />
            </div>
        );
    }
} ;

export default DoughnutChart;
